import { useEffect, useState } from 'react'
import DOMPurify from 'dompurify'

interface MermaidRendererProps {
  chart: string
  className?: string
}

// Global mermaid instance to avoid re-initialization
let mermaidInstance: typeof import('mermaid').default | null = null
let mermaidInitialized = false

export function MermaidRenderer({ chart, className = '' }: MermaidRendererProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [svgContent, setSvgContent] = useState<string>('')

  useEffect(() => {
    let mounted = true
    let timeoutId: NodeJS.Timeout

    const renderMermaid = async () => {
      try {
        setIsLoading(true)
        setError(null)
        setSvgContent('')

        // Add a timeout to prevent infinite loading
        timeoutId = setTimeout(() => {
          if (mounted) {
            console.error('Mermaid rendering timeout after 15 seconds')
            setError('Rendering timeout - diagram took too long to render')
            setIsLoading(false)
          }
        }, 15000) // 15 second timeout

        // Dynamically import mermaid to avoid SSR issues
        if (!mermaidInstance) {
          mermaidInstance = (await import('mermaid')).default
        }

        if (!mounted) {
          return
        }

        // Initialize mermaid with configuration (only once)
        if (!mermaidInitialized) {
          mermaidInstance.initialize({
            startOnLoad: false,
            theme: 'default',
            securityLevel: 'loose',
            fontFamily: 'inherit',
            logLevel: 'error',
          })
          mermaidInitialized = true
        }

        // Generate unique ID for this render
        const uniqueId = `mermaid-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`

        // Trim and validate chart content
        const cleanChart = chart.trim()
        if (!cleanChart) {
          throw new Error('Empty chart content')
        }

        // Render the chart to SVG
        const result = await mermaidInstance.render(uniqueId, cleanChart)

        if (!mounted) {
          return
        }

        // Clear timeout since we succeeded
        clearTimeout(timeoutId)

        // Sanitize the SVG content before setting it
        const sanitizedSvg = DOMPurify.sanitize(result.svg, {
          USE_PROFILES: { svg: true },
          ADD_TAGS: ['foreignObject'], // Allow foreignObject for advanced mermaid features
          ADD_ATTR: ['xmlns', 'xmlns:xlink'], // Allow SVG namespaces
        })

        setSvgContent(sanitizedSvg)
        setIsLoading(false)
      } catch (err) {
        console.error('=== Mermaid rendering error ===', err)
        if (!mounted) return
        if (timeoutId) clearTimeout(timeoutId)

        let errorMessage = 'Failed to render Mermaid diagram'
        if (err instanceof Error) {
          errorMessage = err.message
          console.error('Error details:', err.stack)
        }

        setError(errorMessage)
        setIsLoading(false)
      }
    }

    // Use a small delay to ensure the component has mounted properly
    const delayId = setTimeout(renderMermaid, 100)

    return () => {
      mounted = false
      if (timeoutId) clearTimeout(timeoutId)
      clearTimeout(delayId)
    }
  }, [chart])

  if (error) {
    return (
      <div className={`mermaid-error rounded border border-red-300 bg-red-50 p-4 ${className}`}>
        <p className="text-sm font-medium text-red-700">Mermaid Diagram Error:</p>
        <p className="mt-1 text-sm text-red-600">{error}</p>
        <details className="mt-2">
          <summary className="cursor-pointer text-xs text-red-600">Show diagram code</summary>
          <pre className="mt-1 whitespace-pre-wrap text-xs text-red-500">{chart}</pre>
        </details>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className={`mermaid-loading rounded border border-gray-200 bg-gray-50 p-4 ${className}`}>
        <div className="flex items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <span className="ml-2 text-sm text-gray-600">Rendering diagram...</span>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`mermaid-container flex items-center justify-center ${className}`}
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  )
}
